import { useState, useEffect, useRef, Fragment } from 'react';
import { ChevronRight, ChevronLeft, AlertCircle, Trash2, CheckCircle, Info, X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { SearchableSelect } from './SearchableSelect';
import { getUserProfile } from '../lib/auth';
import { useNotify } from './useNotify';
import {
  checkForDraftReport,
  deleteAllDraftReports,
  type DraftInspection,
} from '../services/inspectionService';

interface ReportSetupProps {
  onStartInspection: (setup: {
    jobSiteName: string;
    jobSiteId: string;
    month: string;
    technicians: string[];
    aspireWorkTicketId?: number | null;
    aspireWorkTicketNumber?: number | null;
  }, draftData?: DraftInspection) => void;
}

interface ServerReport {
  id: string;
  job_site_name: string;
  month: string;
  technicians: string[];
  user_id: string | null;
  is_draft: boolean | null;
  created_at: string;
}

interface JobSiteRow {
  id: string;
  name: string;
  active: boolean | null;
  aspire_property_id: number | null;
}

interface JobSiteOption {
  id: string;
  name: string;
  propertyNumber: number | null;
}

interface ScheduleProperty {
  ticketId: number | null;
  propertyId: number;
  jobSiteId: string | null;
  name: string;
  date: string;
  branch: string | null;
  crewLeader: string | null;
  crewLeaderContactId: number | null;
  ticketNumber: number | null;
  statuses: string[];
  reportStatus: { label: string; color: string; statusType: string } | null;
  linked: boolean;
}

interface WorkTicketDetail {
  ticketId: number;
  ticketNumber: number | null;
  status: string | null;
  crewLeader: string | null;
  ticketNotes: string | null;
  operationNotes: string | null;
  visitNotes: { note: string; author: string | null; createdAt: string | null }[];
}

interface LaneColor {
  border: string;
  chip: string;
  dot: string;
}

// Stable per-branch colour, assigned by sorted branch index. Kept clear of the
// blue selection highlight so a selected card still reads as selected.
const BRANCH_COLORS: LaneColor[] = [
  { border: 'border-l-emerald-500', chip: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300', dot: 'bg-emerald-500' },
  { border: 'border-l-orange-500', chip: 'bg-orange-100 dark:bg-orange-500/20 text-orange-800 dark:text-orange-300', dot: 'bg-orange-500' },
  { border: 'border-l-violet-500', chip: 'bg-violet-100 dark:bg-violet-500/20 text-violet-800 dark:text-violet-300', dot: 'bg-violet-500' },
  { border: 'border-l-cyan-500', chip: 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-800 dark:text-cyan-300', dot: 'bg-cyan-500' },
];

// Stable per-technician colour (Aspire-style), assigned by the sorted crew-leader
// index so every card + row header for one tech shares a hue and techs are easy
// to tell apart. Larger palette than branches since a week can have a dozen+ techs.
const TECH_COLORS: LaneColor[] = [
  { border: 'border-l-blue-500', chip: 'bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300', dot: 'bg-blue-500' },
  { border: 'border-l-emerald-500', chip: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300', dot: 'bg-emerald-500' },
  { border: 'border-l-orange-500', chip: 'bg-orange-100 dark:bg-orange-500/20 text-orange-800 dark:text-orange-300', dot: 'bg-orange-500' },
  { border: 'border-l-violet-500', chip: 'bg-violet-100 dark:bg-violet-500/20 text-violet-800 dark:text-violet-300', dot: 'bg-violet-500' },
  { border: 'border-l-rose-500', chip: 'bg-rose-100 dark:bg-rose-500/20 text-rose-800 dark:text-rose-300', dot: 'bg-rose-500' },
  { border: 'border-l-cyan-500', chip: 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-800 dark:text-cyan-300', dot: 'bg-cyan-500' },
  { border: 'border-l-amber-500', chip: 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300', dot: 'bg-amber-500' },
  { border: 'border-l-teal-500', chip: 'bg-teal-100 dark:bg-teal-500/20 text-teal-800 dark:text-teal-300', dot: 'bg-teal-500' },
  { border: 'border-l-fuchsia-500', chip: 'bg-fuchsia-100 dark:bg-fuchsia-500/20 text-fuchsia-800 dark:text-fuchsia-300', dot: 'bg-fuchsia-500' },
  { border: 'border-l-lime-500', chip: 'bg-lime-100 dark:bg-lime-500/20 text-lime-800 dark:text-lime-300', dot: 'bg-lime-500' },
  { border: 'border-l-sky-500', chip: 'bg-sky-100 dark:bg-sky-500/20 text-sky-800 dark:text-sky-300', dot: 'bg-sky-500' },
  { border: 'border-l-indigo-500', chip: 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-300', dot: 'bg-indigo-500' },
];
// Jobs with no Aspire crew leader — a neutral lane (bg-gray-300+ dot is allowed).
const UNASSIGNED_TECH_COLOR: LaneColor = {
  border: 'border-l-gray-400',
  chip: 'bg-subtle text-theme-secondary',
  dot: 'bg-gray-400',
};

export function ReportSetup({ onStartInspection }: ReportSetupProps) {
  const { t, i18n } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  const { notify, NotifyModal } = useNotify();
  const [jobSites, setJobSites] = useState<JobSiteOption[]>([]);
  const [selectedJobSiteId, setSelectedJobSiteId] = useState<string>('');
  // The Aspire work ticket of the selected schedule card (null for a manual
  // search pick); persisted onto the report record when it's first created.
  const [selectedTicket, setSelectedTicket] = useState<{ id: number; number: number | null } | null>(null);
  const [month, setMonth] = useState<string>(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [technicians, setTechnicians] = useState<string[]>(['']);
  const [schedule, setSchedule] = useState<ScheduleProperty[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState<string | null>(null);
  const [detailItem, setDetailItem] = useState<ScheduleProperty | null>(null);
  const [detailData, setDetailData] = useState<WorkTicketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [draftReport, setDraftReport] = useState<DraftInspection | null>(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [isCheckingDraft, setIsCheckingDraft] = useState(false);

  // Local calendar "today" (YYYY-MM-DD).
  const todayStr = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  })();
  // Default schedule window: a rolling week ahead (today + the next 6 days).
  const SCHEDULE_DAYS = 7;

  // The schedule window. Default = the rolling "from today" view (windowStart =
  // today, isWeekView = false → lanes labelled Today/Mon/etc). Picking a date or
  // using prev/next switches to that date's Monday-start week (Mon→Sun).
  const [windowStart, setWindowStart] = useState<string>(todayStr);
  const [windowDays, setWindowDays] = useState<number>(SCHEDULE_DAYS);
  const [isWeekView, setIsWeekView] = useState<boolean>(false);

  // Per-tech "My jobs" filter. The signed-in user's role + Aspire crew-leader
  // mapping drive the default and whether the filter can match anything.
  const [myJobsOnly, setMyJobsOnly] = useState<boolean>(false);
  const [myCrewLeaderContactId, setMyCrewLeaderContactId] = useState<number | null>(null);
  const [role, setRole] = useState<string | null>(null);
  // Manager/Admin only: narrow the board to one crew leader's jobs (null = all
  // techs). Field techs use the My-jobs toggle instead; managers get this picker.
  const [techFilterContactId, setTechFilterContactId] = useState<number | null>(null);

  // Background-refresh plumbing: when the server reports an Aspire auto-create
  // pass is still running for unlinked scheduled properties, we re-fetch a few
  // times (silently, without the loading state) so the freshly-linked cards
  // refresh in. Bumping `scheduleNonce` re-runs the fetch effect.
  const [scheduleNonce, setScheduleNonce] = useState(0);
  const autoCreateRetriesRef = useRef(0);
  const MAX_SCHEDULE_REFRESHES = 3;
  const SCHEDULE_REFRESH_DELAY_MS = 5000;

  useEffect(() => {
    (async () => {
      try {
        const profile = await getUserProfile();
        if (profile?.email) {
          setTechnicians([profile.email]);
        }
        // Wire up the "My jobs" filter from the signed-in user's role + crew-
        // leader mapping. Field techs (role 'User') default to "My jobs"; if
        // they have no mapping it can't match, so fall back to "All".
        if (profile) {
          setRole(profile.role);
          const tech = profile.role === 'User';
          const crewId = profile.aspireCrewLeaderContactId ?? null;
          setMyCrewLeaderContactId(crewId);
          // Field techs default to "My jobs" when they have a mapping; managers/
          // admins (and unmapped techs) default to "All".
          setMyJobsOnly(tech && crewId != null);
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      }
    })();

    (async () => {
      try {
        const all = await apiClient.get<JobSiteRow[]>('/api/job-sites');
        const active = all
          .filter(s => s.active !== false)
          .map(s => ({ id: s.id, name: s.name, propertyNumber: s.aspire_property_id }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setJobSites(active);
      } catch (err) {
        console.error('Failed to load job sites:', err);
      }
    })();
  }, []);

  // Load the Aspire "Irrigation Check" schedule for the week ahead (forward from
  // today, independent of the chosen inspection date — Aspire reschedules
  // tickets, so a single day is frequently empty). Errors (Aspire unconfigured /
  // unreachable) degrade silently to an empty schedule so the property search
  // remains the fallback.
  // Reset the background-refresh counter whenever the window changes (a new
  // window is a fresh first load, not a retry). Declared before the fetch effect
  // so it runs first on a window change.
  useEffect(() => {
    autoCreateRetriesRef.current = 0;
  }, [windowStart, windowDays]);

  useEffect(() => {
    let cancelled = false;
    // Only show the full loading state on the initial load of a window — a
    // background auto-create refresh updates the cards in place silently.
    const isBackgroundRefresh = autoCreateRetriesRef.current > 0;
    if (!isBackgroundRefresh) setScheduleLoading(true);
    (async () => {
      try {
        const res = await apiClient.get<{ properties: ScheduleProperty[]; autoCreatePending?: boolean }>(
          `/api/integrations/aspire/scheduled-tickets?from=${windowStart}&days=${windowDays}`,
        );
        if (cancelled) return;
        setSchedule(res.properties ?? []);
        // If Aspire is still auto-creating local properties for unlinked rows,
        // poll again shortly (bounded) so they refresh in as linked cards.
        if (res.autoCreatePending && autoCreateRetriesRef.current < MAX_SCHEDULE_REFRESHES) {
          autoCreateRetriesRef.current += 1;
          window.setTimeout(() => {
            if (!cancelled) setScheduleNonce((n) => n + 1);
          }, SCHEDULE_REFRESH_DELAY_MS);
        } else {
          autoCreateRetriesRef.current = 0;
        }
      } catch (err) {
        console.error('Failed to load schedule:', err);
        if (!cancelled) {
          setSchedule([]);
          autoCreateRetriesRef.current = 0;
        }
      } finally {
        if (!cancelled) setScheduleLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [windowStart, windowDays, scheduleNonce]);

  const handleTechnicianChange = (value: string) => {
    setTechnicians([value]);
  };

  const handleSelectScheduled = (item: ScheduleProperty) => {
    if (!item.linked || !item.jobSiteId) {
      // Unlinked Aspire property — can't start an inspection until it's linked.
      notify(tCommon('status.noticeTitle'), t('setup.scheduleUnlinkedHint'), 'warning');
      return;
    }
    setSelectedJobSiteId(item.jobSiteId);
    setSelectedTicket(item.ticketId != null ? { id: item.ticketId, number: item.ticketNumber } : null);
  };

  const openDetails = async (item: ScheduleProperty) => {
    setDetailItem(item);
    setDetailData(null);
    setDetailError(false);
    if (item.ticketId == null) return;
    setDetailLoading(true);
    try {
      const res = await apiClient.get<{ detail: WorkTicketDetail | null }>(
        `/api/integrations/aspire/work-ticket/${item.ticketId}`,
      );
      setDetailData(res.detail ?? null);
    } catch (err) {
      console.error('Failed to load ticket detail:', err);
      setDetailError(true);
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetails = () => {
    setDetailItem(null);
    setDetailData(null);
    setDetailError(false);
  };

  // Resolve the chosen property from the active list, falling back to the
  // schedule (covers a scheduled site whose local job_site was deactivated and
  // is therefore absent from the active-only `jobSites` list).
  const resolveSelectedSite = (): { id: string; name: string } | null => {
    const fromList = jobSites.find((s) => s.id === selectedJobSiteId);
    if (fromList) return { id: fromList.id, name: fromList.name };
    const fromSchedule = schedule.find((s) => s.jobSiteId === selectedJobSiteId);
    if (fromSchedule?.jobSiteId) return { id: fromSchedule.jobSiteId, name: fromSchedule.name };
    return null;
  };

  const checkForExistingReport = async (): Promise<'draft' | 'completed' | 'none'> => {
    if (isCheckingDraft) return 'none';

    setIsCheckingDraft(true);
    try {
      const profile = await getUserProfile();
      if (!profile) return 'none';

      const selectedSite = resolveSelectedSite();
      if (!selectedSite) return 'none';

      const allReports = await apiClient.get<ServerReport[]>('/api/reports');
      const completedReport = allReports.find(
        r => r.user_id === profile.id
          && r.job_site_name === selectedSite.name
          && r.month === month
          && r.is_draft === false,
      );

      if (completedReport) {
        setDraftReport({
          reportId: completedReport.id,
          jobSiteId: selectedJobSiteId,
          jobSiteName: completedReport.job_site_name,
          month: completedReport.month,
          technicians: completedReport.technicians,
          controllers: [],
          lastUpdated: completedReport.created_at,
          totalDraftsForMonth: 1,
        });
        return 'completed';
      }

      const draft = await checkForDraftReport(profile.id, month, selectedJobSiteId);
      if (draft) {
        setDraftReport(draft);
        return 'draft';
      }

      return 'none';
    } catch (error) {
      console.error('Failed to check for existing report:', error);
      return 'none';
    } finally {
      setIsCheckingDraft(false);
    }
  };

  const handleStartInspection = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!technicians[0]?.trim()) {
      notify(tCommon('status.noticeTitle'), t('setup.errorTechnicianRequired'), 'warning');
      return;
    }

    const selectedSite = resolveSelectedSite();
    if (!selectedSite) {
      notify(tCommon('status.noticeTitle'), t('setup.errorPropertyRequired'), 'warning');
      return;
    }

    const reportStatus = await checkForExistingReport();

    if (reportStatus === 'completed') {
      setShowCompletedModal(true);
    } else if (reportStatus === 'draft') {
      setShowDraftModal(true);
    } else {
      onStartInspection({
        jobSiteName: selectedSite.name,
        jobSiteId: selectedJobSiteId,
        month,
        technicians: [technicians[0].trim()],
        aspireWorkTicketId: selectedTicket?.id ?? null,
        aspireWorkTicketNumber: selectedTicket?.number ?? null,
      });
    }
  };

  const handleContinueDraft = () => {
    if (!draftReport) return;

    const selectedSite = jobSites.find(s => s.name === draftReport.jobSiteName);
    if (!selectedSite) return;

    onStartInspection(
      {
        jobSiteName: draftReport.jobSiteName,
        jobSiteId: selectedSite.id,
        month: draftReport.month,
        technicians: draftReport.technicians,
      },
      draftReport
    );
  };

  const handleDeleteDraft = async () => {
    if (!draftReport) return;

    try {
      const profile = await getUserProfile();
      if (!profile) return;

      await deleteAllDraftReports(profile.id, month);
      setShowDraftModal(false);
      setDraftReport(null);

      const selectedSite = resolveSelectedSite();
      if (selectedSite && technicians[0]?.trim()) {
        onStartInspection({
          jobSiteName: selectedSite.name,
          jobSiteId: selectedJobSiteId,
          month,
          technicians: [technicians[0].trim()],
          aspireWorkTicketId: selectedTicket?.id ?? null,
          aspireWorkTicketNumber: selectedTicket?.number ?? null,
        });
      }
    } catch (error) {
      console.error('Failed to delete drafts:', error);
      notify(tCommon('status.errorTitle'), t('setup.errorDeleteDrafts'), 'danger');
    }
  };

  const handleEditCompletedReport = async () => {
    if (!draftReport) return;

    try {
      // Flip the report back to draft so the workflow page can edit it.
      await apiClient.put(`/api/reports/${draftReport.reportId}`, { is_draft: true });
      const selectedSite = jobSites.find(s => s.name === draftReport.jobSiteName);
      if (!selectedSite) return;

      onStartInspection(
        {
          jobSiteName: draftReport.jobSiteName,
          jobSiteId: selectedSite.id,
          month: draftReport.month,
          technicians: draftReport.technicians,
        },
        draftReport,
      );
    } catch (error) {
      console.error('Failed to edit completed report:', error);
      notify(tCommon('status.errorTitle'), t('setup.errorEditReport'), 'danger');
    }
  };

  const handleCancelCompletedEdit = () => {
    setShowCompletedModal(false);
    setDraftReport(null);
  };

  // --- Schedule derived data: crew-leader filter → branch filter → one lane/day ---
  // The crew-leader filter applies first so the branch chip counts below reflect
  // the tickets actually shown.
  const isManagerOrAdmin = role === 'Manager' || role === 'Admin';
  // Managers pick any crew leader from the tech dropdown; field techs use the
  // My-jobs toggle mapped to their own crew leader. Either way it resolves to one
  // CrewLeaderContactID to filter on (null = show everyone).
  const crewFilterContactId = isManagerOrAdmin
    ? techFilterContactId
    : (myJobsOnly && myCrewLeaderContactId != null ? myCrewLeaderContactId : null);
  const myJobsBase = crewFilterContactId != null
    ? schedule.filter((s) => s.crewLeaderContactId === crewFilterContactId)
    : schedule;

  // Distinct crew leaders present in the loaded schedule, for the manager tech
  // filter (the techs who actually have jobs in this window).
  const scheduleCrewLeaders = (() => {
    const byId = new Map<number, string>();
    for (const s of schedule) {
      if (s.crewLeaderContactId != null && !byId.has(s.crewLeaderContactId)) {
        byId.set(s.crewLeaderContactId, s.crewLeader ?? `Contact ${s.crewLeaderContactId}`);
      }
    }
    return [...byId.entries()]
      .map(([contactId, name]) => ({ contactId, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  })();
  // Stable colour per tech, keyed off the sorted crew-leader index so a tech keeps
  // the same hue regardless of the current filter/window.
  const techColorIndex = new Map(scheduleCrewLeaders.map((cl, i) => [cl.contactId, i]));
  const techColor = (contactId: number | null): LaneColor =>
    contactId == null
      ? UNASSIGNED_TECH_COLOR
      : TECH_COLORS[(techColorIndex.get(contactId) ?? 0) % TECH_COLORS.length];

  const branches = [...new Set(myJobsBase.map((s) => s.branch).filter((b): b is string => !!b))].sort();
  const branchColor = (branch: string | null) => {
    const i = branch ? branches.indexOf(branch) : -1;
    return BRANCH_COLORS[(i < 0 ? 0 : i) % BRANCH_COLORS.length];
  };

  // Apply the branch filter on top of the "My jobs" base set (myJobsBase is
  // computed above, before the branch chips, so their counts match).
  const visibleSchedule = branchFilter
    ? myJobsBase.filter((s) => s.branch === branchFilter)
    : myJobsBase;

  const addDaysLocal = (dateStr: string, n: number): string => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d + n);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  // Monday of the week containing `dateStr` (weeks start Monday). JS getDay():
  // Sun=0..Sat=6; shift so Mon=0, then subtract to land on that Monday.
  const mondayOf = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dow = new Date(y, m - 1, d).getDay();
    const offsetToMonday = (dow + 6) % 7;
    return addDaysLocal(dateStr, -offsetToMonday);
  };

  // Jump to the Monday-start week containing `dateStr`.
  const goToWeekOf = (dateStr: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return;
    setWindowStart(mondayOf(dateStr));
    setWindowDays(7);
    setIsWeekView(true);
  };
  const shiftWeek = (deltaWeeks: number) => {
    const base = isWeekView ? windowStart : mondayOf(todayStr);
    goToWeekOf(addDaysLocal(base, deltaWeeks * 7));
  };
  // Reset to the default rolling-from-today view.
  const resetWindow = () => {
    setWindowStart(todayStr);
    setWindowDays(SCHEDULE_DAYS);
    setIsWeekView(false);
  };

  // Columns = each day in the window; rows = each technician (Aspire-style board).
  const days = Array.from({ length: windowDays }, (_, i) => addDaysLocal(windowStart, i));

  // One row per crew leader that has jobs in the visible set (branch/my-jobs
  // filters already applied), in the stable sorted order, plus an "Unassigned"
  // row last for jobs with no crew leader. A field tech on "My jobs" therefore
  // sees just their own row; "All" shows every tech and scrolls.
  const presentTechIds = new Set<number | null>(visibleSchedule.map((s) => s.crewLeaderContactId ?? null));
  const techRows: { contactId: number | null; name: string }[] = scheduleCrewLeaders
    .filter((cl) => presentTechIds.has(cl.contactId))
    .map((cl) => ({ contactId: cl.contactId, name: cl.name }));
  if (presentTechIds.has(null)) {
    techRows.push({ contactId: null, name: t('setup.scheduleUnassignedTech') });
  }
  // Cell lookup: a tech's jobs on one day.
  const cellItems = (contactId: number | null, date: string) =>
    visibleSchedule.filter((s) => (s.crewLeaderContactId ?? null) === contactId && s.date === date);

  const weekdayLabel = (date: string): string => {
    if (date === todayStr) return t('setup.scheduleToday');
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(i18n.language, { weekday: 'short' });
  };
  const shortDate = (date: string): string => {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
  };

  const selectedName = resolveSelectedSite()?.name ?? null;

  // One schedule card. Coloured by its technician (tc) so a tech's jobs read as a
  // set; the crew-leader name lives on the row header now, so it's off the card.
  const renderScheduleCard = (item: ScheduleProperty, tc: LaneColor) => {
    const selected = item.linked && item.jobSiteId === selectedJobSiteId;
    return (
      <div
        key={`${item.propertyId}-${item.date}`}
        role="button"
        tabIndex={0}
        onClick={() => handleSelectScheduled(item)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelectScheduled(item);
          }
        }}
        className={`w-full text-left rounded-lg border border-line border-l-4 ${tc.border} px-2 py-1.5 transition cursor-pointer ${
          selected ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-500/10' : 'bg-surface hover:border-blue-300 dark:hover:border-blue-500/30'
        }`}
      >
        <div className="flex items-start justify-between gap-1">
          <div className="text-[12px] font-semibold text-theme-primary leading-snug line-clamp-2">{item.name}</div>
          <div className="flex items-center gap-0.5 shrink-0">
            {selected && <CheckCircle size={16} className="text-blue-600" />}
            {item.ticketId != null && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openDetails(item); }}
                className="p-0.5 rounded text-theme-secondary hover:text-theme-primary hover:bg-subtle"
                aria-label={t('setup.detailTitle')}
                title={t('setup.detailTitle')}
              >
                <Info size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="mt-1 flex items-center justify-between gap-1">
          {item.branch && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-subtle text-theme-secondary">{item.branch}</span>
          )}
          {item.ticketNumber != null && (
            <span className="text-[10px] text-theme-secondary font-mono">#{item.ticketNumber}</span>
          )}
        </div>
        {item.reportStatus && (
          <div className="mt-1">
            <span
              className="inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded"
              style={{ color: item.reportStatus.color, backgroundColor: `${item.reportStatus.color}1A` }}
              title={item.reportStatus.label}
            >
              {item.reportStatus.label}
            </span>
          </div>
        )}
        {!item.linked && (
          <div className="mt-1 inline-block text-[9px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-500/20 px-1.5 py-0.5 rounded-full">
            {t('setup.scheduleUnlinkedBadge')}
          </div>
        )}
      </div>
    );
  };

  const propertyPicker = (
    <SearchableSelect
      value={selectedJobSiteId}
      onChange={(id) => { setSelectedJobSiteId(id); setSelectedTicket(null); }}
      options={jobSites.map((site) => ({
        value: site.id,
        label: site.name,
        searchText: `${site.name} ${site.propertyNumber ?? ''}`,
      }))}
      placeholder={t('setup.scheduleSearchPlaceholder')}
      noResultsHint={t('setup.propertyNotFoundAspireHint')}
      className="w-full px-3 py-2.5 text-sm border border-line rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  );

  return (
    <div className="h-full flex flex-col gap-2 text-theme-primary">
      {/* Row 1: title + property search (fills the width) + date + technician + Start */}
      <div className="flex items-end gap-3 shrink-0 flex-wrap">
        <div className="shrink-0">
          <h1 className="text-xl font-bold text-theme-primary leading-tight">{t('setup.title')}</h1>
          <p className="text-xs text-theme-secondary">{t('setup.scheduleSubtitle')}</p>
        </div>
        <div className="flex-1 min-w-[200px]">{propertyPicker}</div>
        <div>
          <label className="block text-[11px] font-semibold text-theme-secondary mb-0.5">{t('setup.inspectionDateLabel')}</label>
          <input
            type="date"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm font-medium w-36 focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-theme-secondary mb-0.5">{t('setup.technicianLabel')}</label>
          <input
            type="text"
            value={technicians[0] || ''}
            onChange={(e) => handleTechnicianChange(e.target.value)}
            placeholder={t('setup.technicianPlaceholder')}
            className="px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm font-medium w-48 focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => handleStartInspection()}
          disabled={!selectedName}
          className="shrink-0 flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl font-semibold shadow hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('setup.startButton')}
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Row 2: week nav + selected property + tech/my-jobs filter + branch filter */}
      <div className="flex items-center gap-x-3 gap-y-2 shrink-0 flex-wrap">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => shiftWeek(-1)}
            aria-label={t('setup.schedulePrevWeek')}
            title={t('setup.schedulePrevWeek')}
            className="p-1.5 rounded-lg border border-line bg-surface text-theme-secondary hover:bg-subtle"
          >
            <ChevronLeft size={16} />
          </button>
          <input
            type="date"
            value={isWeekView ? windowStart : todayStr}
            onChange={(e) => { if (e.target.value) goToWeekOf(e.target.value); }}
            aria-label={t('setup.scheduleWeekOfLabel')}
            className="px-2.5 py-1.5 bg-surface text-theme-primary border border-line rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <button
            type="button"
            onClick={() => shiftWeek(1)}
            aria-label={t('setup.scheduleNextWeek')}
            title={t('setup.scheduleNextWeek')}
            className="p-1.5 rounded-lg border border-line bg-surface text-theme-secondary hover:bg-subtle"
          >
            <ChevronRight size={16} />
          </button>
          <button
            type="button"
            onClick={resetWindow}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              isWeekView ? 'bg-surface border border-line text-theme-primary hover:bg-subtle' : 'bg-blue-600 text-white shadow-sm'
            }`}
          >
            {t('setup.scheduleThisWeek')}
          </button>
        </div>

        <div className="flex items-center gap-1.5 text-xs min-w-0">
          <span className="text-theme-secondary shrink-0">{t('setup.scheduleSelectedLabel')}</span>
          {selectedName ? (
            <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-500/10 text-blue-800 dark:text-blue-300 rounded font-semibold truncate border border-blue-200 dark:border-blue-500/30 max-w-[16rem]">
              {selectedName}
            </span>
          ) : (
            <span className="text-theme-secondary italic">{t('setup.scheduleNoneSelected')}</span>
          )}
        </div>

        <div className="ml-auto flex items-center gap-x-3 gap-y-2 flex-wrap">
          {isManagerOrAdmin ? (
            // Managers/Admins: filter the board to any crew leader's jobs.
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-semibold text-theme-secondary">{t('setup.scheduleTechLabel')}</span>
              <select
                value={techFilterContactId != null ? String(techFilterContactId) : ''}
                onChange={(e) => setTechFilterContactId(e.target.value ? Number(e.target.value) : null)}
                aria-label={t('setup.scheduleTechLabel')}
                className="px-2.5 py-1.5 bg-surface text-theme-primary border border-line rounded-lg text-xs font-medium focus:ring-2 focus:ring-blue-500 outline-none max-w-[13rem]"
              >
                <option value="">{t('setup.scheduleTechAll')}</option>
                {scheduleCrewLeaders.map((cl) => (
                  <option key={cl.contactId} value={String(cl.contactId)}>{cl.name}</option>
                ))}
              </select>
            </div>
          ) : (
            // Field techs: My-jobs / All toggle (defaults to My jobs when mapped).
            <div className="flex items-center gap-2">
              {myJobsOnly && myCrewLeaderContactId == null && (
                <span className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-500/20 px-2 py-0.5 rounded-full">
                  {t('setup.scheduleNoCrewLeaderHint')}
                </span>
              )}
              <div className="inline-flex rounded-lg border border-line overflow-hidden">
                <button
                  type="button"
                  onClick={() => setMyJobsOnly(true)}
                  disabled={myCrewLeaderContactId == null}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    myJobsOnly && myCrewLeaderContactId != null ? 'bg-blue-600 text-white' : 'bg-surface text-theme-primary hover:bg-subtle'
                  }`}
                >
                  {t('setup.scheduleMyJobs')}
                </button>
                <button
                  type="button"
                  onClick={() => setMyJobsOnly(false)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    !myJobsOnly || myCrewLeaderContactId == null ? 'bg-blue-600 text-white' : 'bg-surface text-theme-primary hover:bg-subtle'
                  }`}
                >
                  {t('setup.scheduleAllJobs')}
                </button>
              </div>
            </div>
          )}

          {branches.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] text-theme-secondary mr-1">{t('setup.scheduleBranchLabel')}</span>
              <button
                type="button"
                onClick={() => setBranchFilter(null)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                  branchFilter === null ? 'bg-blue-600 text-white shadow-sm' : 'bg-surface border border-line text-theme-primary'
                }`}
              >
                {t('setup.scheduleBranchAll')} · {myJobsBase.length}
              </button>
              {branches.map((b) => {
                const active = branchFilter === b;
                const count = myJobsBase.filter((s) => s.branch === b).length;
                return (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setBranchFilter(active ? null : b)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                      active ? 'bg-blue-600 text-white shadow-sm' : 'bg-surface border border-line text-theme-primary'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${branchColor(b).dot}`} />
                    {b} · {count}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Tech-row schedule board: rows = technicians, columns = days (Aspire-style) */}
      {scheduleLoading ? (
        // Instant shell: the grid header + a few skeleton rows appear immediately
        // (the property search above is already usable) while the schedule loads.
        <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-line bg-subtle">
          <div className="grid w-full" style={{ gridTemplateColumns: `minmax(130px,160px) repeat(${windowDays}, minmax(150px,1fr))` }}>
            <div className="sticky top-0 left-0 z-30 bg-surface border-b border-r border-line px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-theme-secondary">
              {t('setup.scheduleTechColHeader')}
            </div>
            {days.map((date) => {
              const isToday = date === todayStr;
              return (
                <div key={date} className={`sticky top-0 z-20 border-b border-line px-2 py-2 text-center ${isToday ? 'bg-blue-600 text-white' : 'bg-surface text-theme-secondary'}`}>
                  <div className="text-[11px] font-bold uppercase tracking-wide leading-tight">{weekdayLabel(date)}</div>
                  <div className={`text-[10px] ${isToday ? 'text-blue-100' : 'text-theme-secondary'}`}>{shortDate(date)}</div>
                </div>
              );
            })}
            {[0, 1, 2].map((r) => (
              <Fragment key={r}>
                <div className="sticky left-0 z-10 bg-surface border-b border-r border-line px-2 py-2">
                  <div className="h-4 w-24 rounded bg-subtle animate-pulse" />
                </div>
                {days.map((date) => (
                  <div key={date} className="border-b border-line p-1.5">
                    <div className="h-12 rounded-lg bg-subtle animate-pulse" />
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
          <span className="sr-only">{t('setup.scheduleLoading')}</span>
        </div>
      ) : schedule.length === 0 ? (
        <div className="flex-1 min-h-0 flex items-center justify-center text-center text-sm text-theme-secondary border border-dashed border-line rounded-xl px-6">
          {t('setup.scheduleNonePrompt')}
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-line bg-subtle">
          <div className="grid w-full" style={{ gridTemplateColumns: `minmax(130px,160px) repeat(${windowDays}, minmax(150px,1fr))` }}>
            {/* Header row: top-left corner + one column per day (sticky to the top) */}
            <div className="sticky top-0 left-0 z-30 bg-surface border-b border-r border-line px-2 py-2 flex items-end text-[11px] font-semibold uppercase tracking-wide text-theme-secondary">
              {t('setup.scheduleTechColHeader')}
            </div>
            {days.map((date) => {
              const isToday = date === todayStr;
              const count = visibleSchedule.filter((s) => s.date === date).length;
              return (
                <div key={date} className={`sticky top-0 z-20 border-b border-line px-2 py-2 flex items-center justify-between gap-1 ${isToday ? 'bg-blue-600 text-white' : 'bg-surface text-theme-secondary'}`}>
                  <div className="leading-tight">
                    <div className="text-[11px] font-bold uppercase tracking-wide">{weekdayLabel(date)}</div>
                    <div className={`text-[10px] ${isToday ? 'text-blue-100' : 'text-theme-secondary'}`}>{shortDate(date)}</div>
                  </div>
                  {count > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isToday ? 'bg-white/20' : 'bg-subtle text-theme-secondary'}`}>{/* theme-allow: count pill on the accent (blue) today header */}
                      {count}
                    </span>
                  )}
                </div>
              );
            })}
            {/* One row per technician (sticky name column + a cell per day) */}
            {techRows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-theme-secondary" style={{ gridColumn: '1 / -1' }}>
                {t('setup.scheduleNoMatch')}
              </div>
            ) : (
              techRows.map((row) => {
                const tc = techColor(row.contactId);
                return (
                  <Fragment key={row.contactId ?? 'unassigned'}>
                    <div className={`sticky left-0 z-10 bg-surface border-b border-r border-line border-l-4 ${tc.border} px-2 py-2 flex items-start gap-1.5`}>
                      <span className={`mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 ${tc.dot}`} />
                      <span className="text-[12px] font-semibold text-theme-primary leading-tight line-clamp-2">{row.name}</span>
                    </div>
                    {days.map((date) => {
                      const items = cellItems(row.contactId, date);
                      const isToday = date === todayStr;
                      return (
                        <div key={date} className={`border-b border-line p-1.5 space-y-1.5 min-h-[60px] ${isToday ? 'bg-blue-50/60 dark:bg-blue-500/10' : ''}`}>
                          {items.map((item) => renderScheduleCard(item, tc))}
                        </div>
                      );
                    })}
                  </Fragment>
                );
              })
            )}
          </div>
        </div>
      )}

      {showDraftModal && draftReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="bg-yellow-500 text-white px-6 py-4 flex items-center gap-3">
              <AlertCircle size={28} />
              <h2 className="text-xl font-bold">{t('setup.draftFoundTitle')}</h2>
            </div>

            <div className="p-6">
              <p className="text-theme-primary text-lg mb-4">
                {draftReport.totalDraftsForMonth > 1 ? (
                  <Trans
                    i18nKey="setup.draftFoundMessageMultiple"
                    ns="inspection"
                    values={{
                      count: draftReport.totalDraftsForMonth,
                      name: draftReport.jobSiteName,
                      month: draftReport.month,
                    }}
                    components={{ b: <span className="font-bold" /> }}
                  />
                ) : (
                  <Trans
                    i18nKey="setup.draftFoundMessageSingle"
                    ns="inspection"
                    values={{ name: draftReport.jobSiteName, month: draftReport.month }}
                    components={{ b: <span className="font-bold" /> }}
                  />
                )}
              </p>

              <div className="bg-subtle rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-theme-primary mb-2">
                  {draftReport.totalDraftsForMonth > 1
                    ? t('setup.mostRecentDraft')
                    : t('setup.draftDetails')}
                </h3>
                <ul className="space-y-1 text-theme-primary">
                  <li><span className="font-medium">{t('setup.labelProperty')}</span> {draftReport.jobSiteName}</li>
                  <li><span className="font-medium">{t('setup.labelMonth')}</span> {draftReport.month}</li>
                  <li><span className="font-medium">{t('setup.labelTechnicians')}</span> {draftReport.technicians.join(', ')}</li>
                  <li><span className="font-medium">{t('setup.labelControllers')}</span> {draftReport.controllers.length}</li>
                  <li>
                    <span className="font-medium">{t('setup.labelZones')}</span>{' '}
                    {draftReport.controllers.reduce((sum, c) => sum + c.zones.length, 0)}
                  </li>
                  <li>
                    <span className="font-medium">{t('setup.labelLastUpdated')}</span>{' '}
                    {new Date(draftReport.lastUpdated).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </li>
                </ul>
              </div>

              {draftReport.totalDraftsForMonth > 1 && (
                <div className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4 mb-6">
                  <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                    {t('setup.noteDeleteAll', { count: draftReport.totalDraftsForMonth })}
                  </p>
                </div>
              )}

              <p className="text-theme-secondary mb-6">
                {draftReport.totalDraftsForMonth > 1
                  ? t('setup.continuePromptMultiple')
                  : t('setup.continuePromptSingle')}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleContinueDraft}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
                >
                  {t('setup.continueDraft')}
                </button>
                <button
                  onClick={handleDeleteDraft}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors"
                >
                  <Trash2 size={20} />
                  {draftReport.totalDraftsForMonth > 1
                    ? t('setup.deleteAllDrafts', { count: draftReport.totalDraftsForMonth })
                    : t('setup.startNewReport')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompletedModal && draftReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="bg-green-600 text-white px-6 py-4 flex items-center gap-3">
              <CheckCircle size={28} />
              <h2 className="text-xl font-bold">{t('setup.completedFoundTitle')}</h2>
            </div>

            <div className="p-6">
              <p className="text-theme-primary text-lg mb-4">
                <Trans
                  i18nKey="setup.completedFoundMessage"
                  ns="inspection"
                  values={{ name: draftReport.jobSiteName, month: draftReport.month }}
                  components={{ b: <span className="font-bold" /> }}
                />
              </p>

              <div className="bg-subtle rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-theme-primary mb-2">{t('setup.reportDetails')}</h3>
                <ul className="space-y-1 text-theme-primary">
                  <li><span className="font-medium">{t('setup.labelProperty')}</span> {draftReport.jobSiteName}</li>
                  <li><span className="font-medium">{t('setup.labelMonth')}</span> {draftReport.month}</li>
                  <li><span className="font-medium">{t('setup.labelTechnicians')}</span> {draftReport.technicians.join(', ')}</li>
                  <li>
                    <span className="font-medium">{t('setup.labelSubmitted')}</span>{' '}
                    {new Date(draftReport.lastUpdated).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg p-4 mb-6">
                <p className="text-amber-800 dark:text-amber-300 text-sm">{t('setup.noteEditCompleted')}</p>
              </div>

              <p className="text-theme-secondary mb-6">{t('setup.completedPrompt')}</p>

              <div className="flex gap-3">
                <button
                  onClick={handleCancelCompletedEdit}
                  className="flex-1 px-6 py-3 bg-surface border border-line text-theme-primary rounded-lg hover:bg-subtle font-semibold transition-colors"
                >
                  {t('setup.backToSetup')}
                </button>
                <button
                  onClick={handleEditCompletedReport}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
                >
                  {t('setup.editCompletedReport')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailItem && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={closeDetails}
        >
          <div
            className="bg-surface rounded-xl shadow-2xl max-w-md w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-line">
              <h2 className="text-base font-bold text-theme-primary">{t('setup.detailTitle')}</h2>
              <button
                onClick={closeDetails}
                className="p-1 rounded-lg text-theme-secondary hover:bg-subtle"
                aria-label={t('setup.detailClose')}
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto space-y-3 text-sm">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-theme-secondary">{t('setup.detailProperty')}</div>
                <div className="text-theme-primary font-medium">{detailItem.name}</div>
              </div>
              <div className="flex gap-6">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-theme-secondary">{t('setup.detailStatus')}</div>
                  <div className="text-theme-primary">
                    {detailData?.status ?? (detailItem.statuses.length ? detailItem.statuses.join(', ') : '—')}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-theme-secondary">{t('setup.detailTicket')}</div>
                  <div className="text-theme-primary font-mono">{detailItem.ticketNumber != null ? `#${detailItem.ticketNumber}` : '—'}</div>
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-theme-secondary">{t('setup.detailCrewLeader')}</div>
                <div className="text-theme-primary">{detailData?.crewLeader ?? detailItem.crewLeader ?? '—'}</div>
              </div>

              {detailLoading ? (
                <div className="py-4 text-theme-secondary">{t('setup.detailLoading')}</div>
              ) : detailError ? (
                <div className="py-4 text-red-600 dark:text-red-400">{t('setup.detailError')}</div>
              ) : (
                <>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-theme-secondary mb-1">{t('setup.detailVisitNotes')}</div>
                    {detailData?.visitNotes?.length ? (
                      <div className="space-y-1.5">
                        {detailData.visitNotes.map((vn, i) => (
                          <div key={i} className="rounded-lg bg-subtle border border-line p-2">
                            <div className="text-theme-primary whitespace-pre-wrap">{vn.note}</div>
                            {(vn.author || vn.createdAt) && (
                              <div className="text-[11px] text-theme-secondary mt-1">
                                {[
                                  vn.author,
                                  vn.createdAt
                                    ? new Date(vn.createdAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })
                                    : null,
                                ].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-theme-secondary italic">{t('setup.detailNone')}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-theme-secondary mb-1">{t('setup.detailTicketNotes')}</div>
                    {detailData?.ticketNotes ? (
                      <div className="text-theme-primary whitespace-pre-wrap">{detailData.ticketNotes}</div>
                    ) : (
                      <div className="text-theme-secondary italic">{t('setup.detailNone')}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-theme-secondary mb-1">{t('setup.detailOperationNotes')}</div>
                    {detailData?.operationNotes ? (
                      <div className="text-theme-primary whitespace-pre-wrap">{detailData.operationNotes}</div>
                    ) : (
                      <div className="text-theme-secondary italic">{t('setup.detailNone')}</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {NotifyModal}
    </div>
  );
}
